import { Component, computed, forwardRef, inject, OnDestroy, signal, viewChildren } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  Validators
} from '@angular/forms';
import { firstValueFrom, Subscription } from 'rxjs';
import { GroupedBuildingSectionDto } from '@artemis-software/wr-api';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import {
  BuildingSectionDeleteDialogComponent,
} from '@components/dialog/building-section-delete-dialog/building-section-delete-dialog.component';
import { loader } from '@/utils/signals/loader';
import { AbstractFormControl } from '@shared/form-controls/abstract-form-control';
import { AddressCompleteValidator } from '@/utils/validators';
import { AutomaticAddressComponent } from '@shared/form-controls/automatic-address/automatic-address.component';

@Component({
  selector: 'wr-building-section-editor',
  templateUrl: './building-section-editor.component.html',
  styleUrls: ['./building-section-editor.component.scss'],
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BuildingSectionEditorComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => BuildingSectionEditorComponent),
      multi: true,
    }
  ],
})
export class BuildingSectionEditorComponent extends AbstractFormControl<GroupedBuildingSectionDto[]> implements Validator, OnDestroy {
  readonly toastrService = inject(NbToastrService);
  readonly dialogService = inject(NbDialogService);
  readonly formBuilder = inject(FormBuilder);
  protected readonly FormGroup = FormGroup;
  private valueChangesSubscription?: Subscription;

  readonly loader = loader();
  readonly formArray = signal<FormArray|undefined>(undefined);
  readonly formGroup = computed(()=> this.formArray() as FormGroup|undefined);
  readonly addressComponents = viewChildren(AutomaticAddressComponent);
  readonly touched = signal(false);

  readonly controls = computed(()=>{
    this.disabledSignal();
    return this.formArray()?.controls ?? [];
  });

  readonly hasActiveSections = computed(() => {
    const sections = (this.valueSignal() ?? []) as any[];
    return sections.some(section =>
      (section.houseNumbers ?? []).some((hn: any) => !hn.markedForDeletion)
    );
  });

  initForm(value: GroupedBuildingSectionDto[] | undefined) {
    const sections = value ?? [];

    const formArray = new FormArray(sections.map((section) => {
      return new FormGroup({
        street: new FormControl(section.street, [Validators.required]),
        mergeSelected: new FormControl(false),
        automaticAddress: new FormControl({
          zipCode: section.zipCode,
          city: section.city,
          state: section.state
        }, [AddressCompleteValidator.createValidator()]
        ),
        houseNumbers: new FormArray((section.houseNumbers ?? []).map((houseNumber) => new FormGroup({
          number: new FormControl(houseNumber.number, [Validators.required]),
          sectionId: new FormControl(houseNumber.sectionId),
          addressId: new FormControl(houseNumber.addressId),
          markedForDeletion: new FormControl(houseNumber.markedForDeletion ?? false),
          visible: new FormControl(houseNumber.visible ?? true)
        })))
      });
    }));

    this.valueChangesSubscription?.unsubscribe();
    this.valueChangesSubscription = formArray.valueChanges.subscribe((newValue: any[]) => {
      this.value = newValue.map(item => ({
        ...item,
        zipCode: item.automaticAddress?.zipCode,
        city: item.automaticAddress?.city,
        state: item.automaticAddress?.state,
      }));
    });

    this.onValidatorChange?.();
    return formArray;
  }

  override writeValue(value: GroupedBuildingSectionDto[] | undefined) {
    super.writeValue(value);
    this.formArray.set(this.initForm(value));
  }

  override setDisabledState(isDisabled: boolean) {
    super.setDisabledState(isDisabled);
    if(isDisabled){
      this.formArray()?.disable({ emitEvent: false });
    }else{
      this.formArray()?.enable({ emitEvent: false });
    }
  }

  override markAsTouched() {
    super.markAsTouched();
    this.touched.set(true);
    this.formArray()?.markAllAsTouched();
    this.addressComponents().forEach(addressComp => {
      addressComp.markAsTouched();
    });
  }

  private onValidatorChange?: () => void;

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  validate(_control: AbstractControl): ValidationErrors | null {
    if (!this.hasActiveSections()) {
      return { noActiveSections: true };
    }

    const array = this.formArray();
    if (array && array.invalid) {
      return { sectionArrayInvalid: true };
    }
    return null;
  }

  addBuildingSection() {
    this.formArray()?.push(new FormGroup({
      street: new FormControl('', [Validators.required]),
      mergeSelected: new FormControl(false),
      automaticAddress: new FormControl({
        zipCode: '',
        city: '',
        state: ''
      }, [AddressCompleteValidator.createValidator()]),
      houseNumbers: new FormArray([
        new FormGroup({
          sectionId: new FormControl(''),
          addressId: new FormControl(''),
          number: new FormControl('', [Validators.required]),
          visible: new FormControl(true),
        })
      ])
    }));
    this.onValidatorChange?.();
  }

  getSectionControl(index: number){
    return this.formArray()?.at(index) as FormGroup;
  }

  houseNumbers(index: number): FormArray {
    return this.getSectionControl(index).get('houseNumbers') as FormArray;
  }

  async deleteBuildingSection(index: number) {
    const section = this.getSectionControl(index);
    const houseNumbersArray = this.houseNumbers(index);

    const isNewSection = houseNumbersArray.controls.every(
      (houseNumber) => !houseNumber.get('sectionId')?.value
    );

    if (isNewSection) {
      this.formArray()?.removeAt(index);
      this.onValidatorChange?.();
    } else {
      const streetName = section.get('street')?.value || 'unbekannt';

      const buildingSectionIds = houseNumbersArray.controls
        .map(hn => hn.get('sectionId')?.value as string | undefined)
        .filter((id): id is string => !!id);

      const confirmed = await firstValueFrom(this.dialogService.open(BuildingSectionDeleteDialogComponent, {
        context: {
          title: 'Gebäudeteil löschen',
          message: `Ganzen Gebäudeteil ${streetName} löschen?`,
          buildingSectionIds,
        }
      }).onClose);

      if (confirmed) {
        houseNumbersArray.controls.forEach(houseNumberControl => {
          houseNumberControl.get('markedForDeletion')?.setValue(true);
          houseNumberControl.get('number')?.clearValidators();
          houseNumberControl.get('number')?.updateValueAndValidity();
        });

        section.get('street')?.clearValidators();
        section.get('street')?.updateValueAndValidity();
        section.get('automaticAddress')?.clearValidators();
        section.get('automaticAddress')?.updateValueAndValidity();
        this.onValidatorChange?.();
      }
    }
  }

  addHouseNumber(houseNumberArray: FormArray) {
    houseNumberArray.push(new FormGroup({
      sectionId: new FormControl(''),
      addressId: new FormControl(''),
      number: new FormControl('', [Validators.required]),
      visible: new FormControl(true),
    }));
  }

  async deleteHouseNumber(sectionIndex: number, houseNumberIndex: number) {
    const houseNumberArray = this.houseNumbers(sectionIndex);
    const houseNumber = houseNumberArray.at(houseNumberIndex);
    const sectionId = houseNumber.get('sectionId')?.value as string | undefined;
    const isNew = !sectionId;

    if (isNew) {
      houseNumberArray.removeAt(houseNumberIndex);
    } else {
      const confirmed = await firstValueFrom(this.dialogService.open(BuildingSectionDeleteDialogComponent, {
        context: {
          title: 'Hausnummer löschen',
          message: `Hausnummer ${houseNumber.value.number || ''} löschen?`,
          buildingSectionIds: [sectionId],
        }
      }).onClose);

      if (confirmed) {
        houseNumber.get('markedForDeletion')?.setValue(true);
        houseNumber.get('number')?.clearValidators();
        houseNumber.get('number')?.updateValueAndValidity();
        this.onValidatorChange?.();
      }
    }
  }

  hasHouseNumberError(sectionIndex: number, houseNumberIndex: number): boolean {
    const control = this.houseNumbers(sectionIndex).at(houseNumberIndex).get('number');
    return !!(control?.invalid && (control.dirty || control.touched));
  }

  sectionHasNonDeletedHouseNumbers(section: FormGroup){
    const houseNumberArray = section.get('houseNumbers') as FormArray;
    return houseNumberArray.controls.some(control => control.get('markedForDeletion')?.value !== true);
  }

  hasControlError(control: AbstractControl | null): boolean {
    return !!(control?.invalid && (control.dirty || control.touched));
  }

  ngOnDestroy(): void {
    this.valueChangesSubscription?.unsubscribe();
  }

}
